import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { useDialogControllers } from '@machobear/design-system'
import { colors } from '@/styles'
import useProfile from '@/hooks/use-profile'
import useGetInsights from '@/hooks/use-get-insights'
import { SponsoredListingProvider } from '@/features/SponsoredListing/modals/sponsor-wizard'
import { GraphBar, RocketIcon } from '@/components/icons'
import { BoldText, Link, Title } from '../styles'
import SponsorDialogs from '../components/sponsor-dialogs'

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  border: 1px solid ${colors.grey88};
  border-top: 8px solid ${colors.blue16};
  border-radius: 6px;
  gap: 16px;
  background: white;
`

const Upper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Statistic = styled.div`
  font-size: 16px;
  font-weight: 700;
`

const Text = styled.span`
  font-size: 14px;
  color: ${colors.grey40};
`

const SmallText = styled.span`
  font-size: 12px;
  font-weight: 400;
`

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 24px;
  border-radius: 0 0 6px 6px;
  border-top: 1px solid ${colors.grey88};
  background: ${colors.background};

  @media screen and (min-width: 767px) {
    display: none;
  }
`

const Button = styled.button`
  width: 100%;
  border-radius: 6px;
  background: ${colors.blue16};
  border: none;
  box-shadow:
    0px 2px 4px rgba(19, 43, 62, 0.25),
    0px 6px 6px 2px rgba(19, 43, 62, 0.1);
  color: ${colors.white};
  padding: 12px;
  font-family: 'Inter';
  font-weight: 700;
  font-size: 12px;
`

const Contact = styled.span`
  color: ${colors.grey40};
  font-size: 12px;
  width: 100%;
  text-align: left;
`

const InsightsCard = ({ listingId }: any) => {
  const { applications, priceInfo } = useGetInsights(listingId)
  const profileData = useProfile()
  const { t } = useTranslation()

  const applicationCount = applications ? Object.keys(applications?.applications).length : 0
  const difference = priceInfo?.difference
  const ppsf = priceInfo?.average
  const isAboveAverage = priceInfo?.aboveAverage

  const { visible, showModal, closeModal } = useDialogControllers({
    billingUser: false,
    sponsoredListing: false,
    nextSteps: false
  })

  const handleBoostListing = () => {
    if (profileData?.billing_user_valid) {
      showModal('sponsoredListing')()
    } else {
      showModal('billingUser')()
    }
  }

  return (
    <>
      <Wrapper>
        <Upper>
          <Row>
            <GraphBar />
            <Title>{t('l.listing.dashboard.insights.title', 'Insights')}</Title>
          </Row>

          <Row>
            <Col>
              <Statistic>{applicationCount}</Statistic>
              <Text>{t('l.listing.view.applications_received.text', 'Applications received')}</Text>
            </Col>
          </Row>

          {ppsf !== '' && (
            <Row>
              <Col>
                <Statistic>
                  {difference}%{' '}
                  {isAboveAverage ? t('common.higher', 'higher') : t('common.lower', 'lower')}
                </Statistic>
                <SmallText>
                  {
                    <Trans i18nKey="l.listing.dashboard.insights.statistic.price.text">
                      than the average rent price of similar listings near you: ${{ ppsf }}/sqft
                    </Trans>
                  }
                </SmallText>
              </Col>
            </Row>
          )}
        </Upper>

        <Bottom>
          <Row style={{ gap: '8px' }}>
            <RocketIcon width={20} />
            <BoldText>
              {t(
                'l.listing.dashboard.more_inquiries.text',
                'Get up to 300% more inquiries and find renters faster.'
              )}
            </BoldText>
          </Row>

          <Button onClick={handleBoostListing}>
            {t('l.listing.dashboard.sponsored.boost', 'Boost Your Listing')}
          </Button>

          <Trans i18nKey="l.listing.dashboard.contact_support.text">
            <Contact>
              Need Help? <Link>Contact liv.rent support.</Link>
            </Contact>
          </Trans>
        </Bottom>
      </Wrapper>
      <SponsoredListingProvider initialStep={0} initialValues={{}} isCreateListing={false}>
        <SponsorDialogs
          listingId={listingId}
          visible={visible}
          showModal={showModal}
          closeModal={closeModal}
        />
      </SponsoredListingProvider>
    </>
  )
}

export default InsightsCard
